import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Button, Divider,
  CircularProgress, Alert, ToggleButton, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Save as SaveIcon, Refresh as RefreshIcon,
  EventAvailable as WeekIcon, Payments as CollectionsIcon,
  HowToReg as RegisterIcon, School as ExamIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// ─────────────────────────────────────────────────────────────────
// SCHOOL WEEK — which days of the week each module operates on.
//
// WHY THIS SCREEN EXISTS
//   "No school on Saturday" was hard-coded, and that was wrong in both
//   directions at once. Collections genuinely must stop at the weekend
//   — nothing is served, so nothing may be charged, and a compulsory
//   daily fee left openable on a Saturday quietly bills every learner
//   for a day they were never at school. But the register and score
//   entry must NOT stop: a teacher with a week of unmarked registers
//   sits down on Saturday to catch up, and schools that run Saturday
//   classes take a real roll that morning.
//
//   One rule could not serve both, and a constant in the code could not
//   serve a school that sits on Saturday. So it comes here, where an
//   administrator can answer it per module.
//
// WHAT TURNING A DAY OFF ACTUALLY DOES
//   Nothing retroactive. It decides which days may be OPENED from now
//   on; a day already worked keeps everything recorded against it.
// ─────────────────────────────────────────────────────────────────

// Monday first — that is how a school week reads — but the array the
// API speaks is indexed from Sunday (JS getDay()), so each entry
// carries the index it maps to rather than relying on position.
const DAY_ORDER = [
  { i: 1, short: 'Mon' }, { i: 2, short: 'Tue' }, { i: 3, short: 'Wed' },
  { i: 4, short: 'Thu' }, { i: 5, short: 'Fri' }, { i: 6, short: 'Sat' },
  { i: 0, short: 'Sun' },
];

const MODULE_ICONS = {
  collections: CollectionsIcon,
  register:    RegisterIcon,
  examination: ExamIcon,
};

const sameDays = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length
  && a.every((v, i) => !!v === !!b[i]);

export default function SchoolWeekCard() {
  const [modules, setModules] = useState([]);   // as loaded from the server
  const [draft, setDraft]     = useState({});   // { moduleKey: [sun..sat] }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await API.get('/school-week');
      const mods = r.data?.modules || [];
      setModules(mods);
      setDraft(Object.fromEntries(mods.map(m => [m.key, [...(m.days || [])]])));
    } catch (e) {
      setError(swal.pickError(e, 'Could not load the school week'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Which modules the administrator has actually changed. Saving sends
  // only these, so an untouched module's updated_by/updated_at is not
  // rewritten by someone who came to change a different one.
  const changed = useMemo(
    () => modules.filter(m => !sameDays(draft[m.key], m.days)).map(m => m.key),
    [modules, draft]
  );

  const toggleDay = (moduleKey, dayIndex) => {
    setDraft(prev => {
      const days = [...(prev[moduleKey] || [])];
      days[dayIndex] = !days[dayIndex];
      return { ...prev, [moduleKey]: days };
    });
  };

  const save = async () => {
    // A module with every day off can never be worked — an easy slip to
    // make and a hard one to diagnose later, so it is caught here as
    // well as on the server rather than only there.
    const empty = changed.find(k => !(draft[k] || []).some(Boolean));
    if (empty) {
      const label = modules.find(m => m.key === empty)?.label || empty;
      swal.warning(`${label} must run on at least one day of the week.`, 'Nothing would run');
      return;
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(changed.map(k => [k, draft[k]]));
      const r = await API.put('/school-week', { modules: payload });
      const mods = r.data?.modules || [];
      setModules(mods);
      setDraft(Object.fromEntries(mods.map(m => [m.key, [...(m.days || [])]])));
      swal.success('School week updated');
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not save the school week'));
    } finally { setSaving(false); }
  };

  if (loading) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box>;
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}
        action={<Button size="small" startIcon={<RefreshIcon />} onClick={load}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  return (
    <Card sx={{ borderRadius: 2 }}>
      <Box sx={{
        px: { xs: 2, md: 2.5 }, py: 1.8,
        borderBottom: 1, borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      }}>
        <WeekIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            School Week
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Which days of the week each part of the system runs on
          </Typography>
        </Box>
        {changed.length > 0 && (
          <Chip size="small" color="warning" variant="outlined" sx={{ fontWeight: 700 }}
            label={`${changed.length} unsaved`} />
        )}
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Reload
        </Button>
        <Button size="small" variant="contained" startIcon={<SaveIcon />}
          onClick={save} disabled={saving || changed.length === 0}
          sx={{ textTransform: 'none', fontWeight: 800 }}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>

      <CardContent sx={{ pt: 2.5 }}>
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5, mb: 2.5 }}>
          Turning a day <b>off</b> stops that part of the system for that day: nothing can
          be opened, entered or charged, and no arrears build up. It changes nothing that
          was already recorded — only which days can be worked from now on.
        </Alert>

        <Stack divider={<Divider flexItem />} spacing={0}>
          {modules.map((m) => {
            const Icon = MODULE_ICONS[m.key] || WeekIcon;
            const days = draft[m.key] || [];
            const dirty = !sameDays(days, m.days);
            const noneOn = !days.some(Boolean);
            return (
              <Box key={m.key} sx={{ py: 2.2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: 1.2,
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                        color: 'primary.main',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon fontSize="small" />
                      </Box>
                      <Typography fontWeight={800}>{m.label}</Typography>
                      {dirty && (
                        <Chip size="small" color="warning" label="changed"
                          sx={{ height: 18, fontSize: 10, fontWeight: 800 }} />
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary"
                      sx={{ maxWidth: 560 }}>
                      {m.blurb}
                    </Typography>
                  </Box>

                  {/* The week itself. Deliberately seven independent
                      toggles rather than a "weekends off" switch — a
                      school that sits on Saturday but not Sunday is
                      ordinary, and so is one that closes on a Friday.
                      Plain ToggleButtons in a row, NOT a
                      ToggleButtonGroup: the group clones its children
                      and replaces their onChange with its own, so a
                      per-day handler attached to the button never
                      fires. */}
                  <Box>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      {DAY_ORDER.map(({ i, short }) => {
                        const on = !!days[i];
                        return (
                          <Tooltip key={i}
                            title={on ? `${m.label} runs on ${short}` : `${m.label} is off on ${short}`}>
                            <ToggleButton size="small" value={short} selected={on}
                              onClick={() => toggleDay(m.key, i)}
                              sx={{
                                px: 1.4, borderRadius: 1.2,
                                fontWeight: 800, textTransform: 'none',
                                '&.Mui-selected': {
                                  bgcolor: (t) => alpha(t.palette.success.main, 0.16),
                                  color: 'success.dark',
                                  '&:hover': {
                                    bgcolor: (t) => alpha(t.palette.success.main, 0.24),
                                  },
                                },
                              }}>
                              {short}
                            </ToggleButton>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                    <Typography variant="caption"
                      color={noneOn ? 'error.main' : 'text.secondary'}
                      sx={{ display: 'block', mt: 0.6, fontWeight: 700 }}>
                      {noneOn
                        ? 'Never runs — turn at least one day on'
                        : `Runs ${describe(days)}`}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

// Mirrors the server's describeDays so the caption updates as the
// administrator clicks, instead of going stale until they save. The
// server's copy is still the one that ends up in refusal messages.
function describe(days) {
  const order = DAY_ORDER.map(d => d.i);
  const on = order.filter(i => days[i]);
  if (on.length === 0) return 'no days';
  if (on.length === 7) return 'every day';
  const full = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const positions = on.map(i => order.indexOf(i));
  const contiguous = positions.every((p, n) => n === 0 || p === positions[n - 1] + 1);
  if (contiguous && on.length > 2) return `${full[on[0]]} to ${full[on[on.length - 1]]}`;
  const names = on.map(i => full[i]);
  if (names.length === 1) return `${names[0]}s only`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
